/**
 * Custom Button Component
 * Extends Ant Design Button with custom styling
 */

import { Button as AntButton, ButtonProps as AntButtonProps } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import './CustomButton.scss';

export interface CustomButtonProps extends Omit<AntButtonProps, 'type' | 'variant' | 'iconPosition'> {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost' | 'link';
  /** Full width button */
  fullWidth?: boolean;
  /** Custom icon position */
  iconPosition?: 'left' | 'right';
}

/**
 * CustomButton Component
 * 
 * @example
 * ```tsx
 * <CustomButton variant="primary" onClick={handleClick}>
 *   Click Me
 * </CustomButton>
 * ```
 */
export const CustomButton: React.FC<CustomButtonProps> = ({
  variant = 'primary',
  fullWidth = false,
  iconPosition = 'left',
  className,
  children,
  loading,
  icon,
  ...rest
}) => {
  const buttonClasses = classNames(
    'custom-button',
    `custom-button--${variant}`,
    {
      'custom-button--full-width': fullWidth,
      'custom-button--icon-right': iconPosition === 'right' && icon,
    },
    className
  );

  const loadingIcon = loading ? <LoadingOutlined /> : null;

  return (
    <AntButton
      {...rest}
      className={buttonClasses}
      loading={loading}
      icon={!loading && icon ? icon : loadingIcon}
    >
      {children}
    </AntButton>
  );
};

export default CustomButton;

